<template>
  <div class="lyrics-extractor">
    <!-- 가사 추출 버튼 (autoStart가 false일 때만 표시) -->
    <button
      v-if="!autoStart && !isExtracting && !lyrics.length"
      @click="startExtraction"
      class="extract-btn"
      :disabled="!canExtract"
    >
      <span class="icon">🎤</span>
      <span>AI 가사 자동 추출</span>
    </button>

    <!-- 추출 중 상태 -->
    <div v-if="isExtracting" class="extraction-progress">
      <div class="spinner"></div>
      <div class="progress-text">
        <p class="status">{{ statusText }}</p>
        <p class="time">{{ elapsedTime }}초 경과...</p>
      </div>
      <button @click="cancelExtraction" class="cancel-btn">취소</button>
    </div>

    <!-- 추출 완료 - 가사 표시 -->
    <div v-if="lyrics.length" class="lyrics-result">
      <div class="lyrics-header">
        <h3>추출된 가사</h3>
        <div class="actions">
          <button @click="copyLyrics" class="action-btn">
            <span class="icon">📋</span>
            복사
          </button>
          <button @click="resetExtraction" class="action-btn">
            <span class="icon">🔄</span>
            다시 추출
          </button>
        </div>
      </div>

      <div class="lyrics-content">
        <div
          v-for="(segment, index) in lyrics"
          :key="index"
          class="lyric-segment"
        >
          <span class="timestamp">{{ formatTime(segment.start) }}</span>
          <div class="text-row">
            <div class="text-column">
              <span class="label">원본</span>
              <input
                type="text"
                v-model="segment.text"
                class="text-input"
                placeholder="원본 가사"
              />
            </div>
            <div class="text-column">
              <span class="label">변환</span>
              <input
                type="text"
                v-model="segment.trans_text"
                class="text-input"
                placeholder="변환된 가사"
              />
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- 오류 표시 -->
    <div v-if="error" class="error-message">
      <span class="icon">⚠️</span>
      <span>{{ error }}</span>
      <button @click="resetExtraction" class="retry-btn">다시 시도</button>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted } from 'vue'
import { extractLyrics, pollLyricsStatus } from '@/services/lyricsService'

const props = defineProps({
  videoId: {
    type: String,
    required: true
  },
  startTime: {
    type: Number,
    required: true
  },
  endTime: {
    type: Number,
    required: true
  },
  outputLanguage: {
    type: String,
    default: 'ko'
  },
  autoStart: {
    type: Boolean,
    default: false  // 기본값은 자동 시작 안 함
  }
})

const emit = defineEmits(['lyrics-extracted', 'extraction-error'])

// 상태
const isExtracting = ref(false)
const lyrics = ref([])
const error = ref(null)
const statusText = ref('가사 추출 준비 중...')
const elapsedTime = ref(0)
let pollingAborted = false
let timerInterval = null

// 추출 가능 여부
const canExtract = computed(() => {
  return props.videoId && props.startTime >= 0 && props.endTime > props.startTime
})

// 컴포넌트 마운트 시 자동 시작 여부 확인
onMounted(() => {
  if (props.autoStart && canExtract.value) {
    startExtraction()
  }
})

// 가사 추출 시작
async function startExtraction() {
  if (!canExtract.value) return

  isExtracting.value = true
  error.value = null
  pollingAborted = false
  elapsedTime.value = 0

  // 타이머 시작
  timerInterval = setInterval(() => {
    elapsedTime.value++
  }, 1000)

  try {
    statusText.value = '가사 추출 요청 중...'

    // 1단계: 추출 요청
    const { job_id } = await extractLyrics(
      props.videoId,
      props.startTime,
      props.endTime,
      props.outputLanguage
    )

    if (pollingAborted) return

    statusText.value = 'AI가 가사를 분석 중입니다...'

    // 2단계: 폴링으로 결과 대기
    const result = await pollLyricsStatus(
      job_id,
      (status, attempt, maxAttempts) => {
        if (pollingAborted) {
          throw new Error('사용자가 취소했습니다.')
        }

        if (status === 'processing') {
          statusText.value = `AI가 가사를 추출하고 있습니다... (${attempt}/${maxAttempts})`
        }
      }
    )

    if (pollingAborted) return

    // 성공
    lyrics.value = result
    emit('lyrics-extracted', result)

  } catch (err) {
    if (!pollingAborted) {
      error.value = err.message || '가사 추출에 실패했습니다.'
      emit('extraction-error', err)
    }
  } finally {
    isExtracting.value = false
    clearInterval(timerInterval)
  }
}

// 추출 취소
function cancelExtraction() {
  pollingAborted = true
  isExtracting.value = false
  clearInterval(timerInterval)
  statusText.value = '취소됨'
}

// 초기화 (다시 추출)
function resetExtraction() {
  lyrics.value = []
  error.value = null
  elapsedTime.value = 0
  pollingAborted = false
}

// 가사 복사 (원본 + 변환 둘 다)
function copyLyrics() {
  const text = lyrics.value
    .map(seg => {
      const time = formatTime(seg.start)
      const original = seg.text
      const translated = seg.trans_text || seg.text
      return `[${time}]\n원본: ${original}\n변환: ${translated}`
    })
    .join('\n\n')

  navigator.clipboard.writeText(text)
    .then(() => {
      alert('가사가 클립보드에 복사되었습니다!')
    })
    .catch(err => {
      console.error('복사 실패:', err)
    })
}

// 시간 포맷 (초 → MM:SS)
function formatTime(seconds) {
  const mins = Math.floor(seconds / 60)
  const secs = Math.floor(seconds % 60)
  return `${mins}:${secs.toString().padStart(2, '0')}`
}

// 컴포넌트 언마운트 시 타이머 정리
onUnmounted(() => {
  clearInterval(timerInterval)
})
</script>

<style scoped>
.lyrics-extractor {
  margin: 1rem 0;
}

.extract-btn {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem 1.5rem;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  border: none;
  border-radius: 8px;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  transition: transform 0.2s, box-shadow 0.2s;
}

.extract-btn:hover:not(:disabled) {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(102, 126, 234, 0.4);
}

.extract-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.extraction-progress {
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 1rem;
  background: #f8f9fa;
  border-radius: 8px;
  border: 2px solid #e9ecef;
}

.spinner {
  width: 24px;
  height: 24px;
  border: 3px solid #e9ecef;
  border-top-color: #667eea;
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

.progress-text {
  flex: 1;
}

.progress-text .status {
  font-weight: 600;
  color: #495057;
  margin: 0 0 0.25rem 0;
}

.progress-text .time {
  font-size: 0.875rem;
  color: #6c757d;
  margin: 0;
}

.cancel-btn {
  padding: 0.5rem 1rem;
  background: #dc3545;
  color: white;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  font-weight: 500;
}

.cancel-btn:hover {
  background: #c82333;
}

.lyrics-result {
  margin-top: 1rem;
}

.lyrics-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1rem;
}

.lyrics-header h3 {
  margin: 0;
  font-size: 1.25rem;
  color: #212529;
}

.actions {
  display: flex;
  gap: 0.5rem;
}

.action-btn {
  display: flex;
  align-items: center;
  gap: 0.25rem;
  padding: 0.5rem 1rem;
  background: white;
  border: 1px solid #dee2e6;
  border-radius: 6px;
  cursor: pointer;
  font-size: 0.875rem;
  transition: all 0.2s;
}

.action-btn:hover {
  background: #f8f9fa;
  border-color: #adb5bd;
}

.lyrics-content {
  background: white;
  border: 1px solid #dee2e6;
  border-radius: 8px;
  padding: 1rem;
  max-height: 400px;
  overflow-y: auto;
}

.lyric-segment {
  display: flex;
  gap: 1rem;
  padding: 0.75rem 0.5rem;
  border-bottom: 1px solid #f1f3f5;
}

.lyric-segment:last-child {
  border-bottom: none;
}

.timestamp {
  font-family: 'Courier New', monospace;
  font-size: 0.875rem;
  color: #6c757d;
  min-width: 50px;
  flex-shrink: 0;
}

.text-row {
  flex: 1;
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1rem;
}

.text-column {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
}

.label {
  font-size: 0.75rem;
  font-weight: 600;
  color: #6c757d;
}

.text-column:first-child .label {
  color: #0066cc;
}

.text-column:last-child .label {
  color: #28a745;
}

.text-input {
  width: 100%;
  padding: 0.5rem;
  border: 1px solid #dee2e6;
  border-radius: 4px;
  font-size: 0.875rem;
  color: #212529;
  line-height: 1.4;
  transition: border-color 0.2s ease;
}

.text-input:focus {
  outline: none;
  border-color: #667eea;
  box-shadow: 0 0 0 2px rgba(102, 126, 234, 0.1);
}

.error-message {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 1rem;
  background: #fff3cd;
  border: 1px solid #ffc107;
  border-radius: 8px;
  color: #856404;
}

.error-message .icon {
  font-size: 1.5rem;
}

.retry-btn {
  margin-left: auto;
  padding: 0.5rem 1rem;
  background: #ffc107;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  font-weight: 500;
}

.retry-btn:hover {
  background: #e0a800;
}

.icon {
  font-size: 1.2em;
}
</style>
